import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { NumericInputWithSlider } from '../NumericInputWithSlider';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { visualConfig } from '../../config/visualConfig';

interface PalletInputsTabProps {
  usePallets: boolean;
  setUsePallets: (value: boolean) => void;
  palletLength: number;
  palletWidth: number;
  palletHeight: number;
  maxStackHeight: number;
  maxStackWeight: number;
  unitSystem: 'metric' | 'imperial';
  setPalletLengthAndReset: (value: number) => void;
  setPalletWidthAndReset: (value: number) => void;
  setPalletHeightAndReset: (value: number) => void;
  setMaxStackHeightAndReset: (value: number) => void;
  setMaxStackWeightAndReset: (value: number) => void;
  goToPreviousTab: () => void;
  goToNextTab: () => void;
}

export function PalletInputsTab({
  usePallets,
  setUsePallets,
  palletLength,
  palletWidth,
  palletHeight,
  maxStackHeight,
  maxStackWeight,
  unitSystem,
  setPalletLengthAndReset,
  setPalletWidthAndReset,
  setPalletHeightAndReset,
  setMaxStackHeightAndReset,
  setMaxStackWeightAndReset,
  goToPreviousTab,
  goToNextTab
}: PalletInputsTabProps) {
  const colors = visualConfig.colors.pallet;
  
  const commonPalletSizes = [
    { label: "Euro Pallet (120 x 80 cm)", length: 120, width: 80 },
    { label: "Standard US Pallet (48 x 40 in)", length: 121.92, width: 101.6 },
    { label: "Asia Pallet (110 x 110 cm)", length: 110, width: 110 },
    { label: "Australian Pallet (116.5 x 116.5 cm)", length: 116.5, width: 116.5 },
  ];

  return (
    <Card className={`${visualConfig.layout.card.minHeight} flex flex-col ${visualConfig.layout.card.background} ${visualConfig.layout.card.shape} overflow-hidden`}>
      <CardHeader className={`bg-gradient-to-r ${colors.gradient} text-white ${visualConfig.layout.header.padding}`}>
        <CardTitle className={visualConfig.layout.header.title}>Pallet Dimensions & Capacity</CardTitle>
        <CardDescription className={`text-green-100 ${visualConfig.layout.header.description}`}>
          Enter the dimensions and stacking properties of your pallets.
        </CardDescription>
      </CardHeader>
      <CardContent className={`${visualConfig.layout.content.spacing} ${visualConfig.layout.content.padding}`}>
        <div className={`${visualConfig.interactive.form.padding} ${visualConfig.interactive.form.background}`}>
          <label className="flex items-center space-x-4 cursor-pointer">
            <input
              type="checkbox"
              checked={usePallets}
              onChange={(e) => {
                setUsePallets(e.target.checked);
              }}
              className={`${visualConfig.interactive.checkbox.size} ${colors.checkbox} ${visualConfig.interactive.checkbox.shape} focus:ring-green-500 ${visualConfig.interactive.checkbox.focus}`}
            />
            <span className={`${visualConfig.typography.subsectionTitle} ${visualConfig.colors.neutral.text.primary}`}>Use Pallets for Loading</span>
          </label>
          <p className={`${visualConfig.typography.body} ${visualConfig.colors.neutral.text.secondary} mt-3 ml-11 lg:ml-10`}>
            Enable to pack cartons onto pallets before loading into container. Disable for direct container loading.
          </p>
        </div>

        <div className={visualConfig.interactive.form.spacing}>
          <Label htmlFor="pallet-size-select" className={`${visualConfig.typography.label} ${!usePallets ? visualConfig.colors.neutral.text.disabled : ''}`}>Common Pallet Sizes</Label>
          <Select onValueChange={(value) => {
            if (!usePallets) return;
            const selectedSize = commonPalletSizes.find(size => `${size.length}x${size.width}` === value);
            if (selectedSize) {
              setPalletLengthAndReset(selectedSize.length);
              setPalletWidthAndReset(selectedSize.width);
            }
          }} value={`${palletLength}x${palletWidth}`} disabled={!usePallets}>
            <SelectTrigger id="pallet-size-select" className={`${visualConfig.interactive.select.shape} ${!usePallets ? visualConfig.interactive.select.disabled : ''}`}>
              <SelectValue placeholder="Select a common pallet size" />
            </SelectTrigger>
            <SelectContent>
              {commonPalletSizes.map((size) => (
                <SelectItem key={`${size.length}x${size.width}`} value={`${size.length}x${size.width}`}>
                  {size.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <NumericInputWithSlider
          id="pallet-length"
          baseLabel="Custom Pallet Length"
          value={palletLength}
          onChange={setPalletLengthAndReset}
          min={50}
          max={150}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
          disabled={!usePallets}
        />
        <NumericInputWithSlider
          id="pallet-width"
          baseLabel="Custom Pallet Width"
          value={palletWidth}
          onChange={setPalletWidthAndReset}
          min={50}
          max={150}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
          disabled={!usePallets}
        />
        <NumericInputWithSlider
          id="pallet-height"
          baseLabel="Pallet Height"
          value={palletHeight}
          onChange={setPalletHeightAndReset}
          min={10}
          max={30}
          step={unitSystem === 'metric' ? 0.1 : 0.01}
          unitSystem={unitSystem}
          unitType="dimension"
          disabled={!usePallets}
        />
        <NumericInputWithSlider
          id="max-stack-height"
          baseLabel="Max Stack Height"
          value={maxStackHeight}
          onChange={setMaxStackHeightAndReset}
          min={100}
          max={300}
          step={unitSystem === 'metric' ? 1 : 0.1}
          unitSystem={unitSystem}
          unitType="dimension"
          disabled={!usePallets}
        />
        <NumericInputWithSlider
          id="max-stack-weight"
          baseLabel="Max Stack Weight"
          value={maxStackWeight}
          onChange={setMaxStackWeightAndReset}
          min={500}
          max={2000}
          step={unitSystem === 'metric' ? 50 : 1}
          unitSystem={unitSystem}
          unitType="weight"
          disabled={!usePallets}
        />
      </CardContent>
      <div className={`${visualConfig.layout.footer.layout} ${visualConfig.layout.footer.padding}`}>
        <button
          onClick={goToPreviousTab}
          className={`${visualConfig.interactive.button.base} ${visualConfig.colors.neutral.previous} ${visualConfig.interactive.button.focus} focus:ring-gray-200`}
        >
          Previous
        </button>
        <button
          onClick={goToNextTab}
          className={`${visualConfig.interactive.button.base} ${colors.button} ${visualConfig.interactive.button.focus} ${colors.ring}`}
        >
          Next
        </button>
      </div>
    </Card>
  );
} 